import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';

import { ReactSelectValue } from 'types/forms';

import { formatDollars } from 'util/currency';
import { formatDate, DATE_FORMATS } from 'util/dates';
import { DIY, FULL_SERVICE, HYBRID } from '../SurveyTypePanel/SurveyTypePanel';
import Button from 'components/common/forms/Button';
import LogoWhiteText from 'components/common/LogoWhiteText';
import Icon from 'components/common/Icon';
import QuoteSummaryPDF from './QuoteSummaryPDF';
import { BillingServiceName, BillingPrice } from 'services/backend/billing';
import { getOptionPrice, getOptionLabel } from '../helpers';

interface QuoteSummaryPanelProps {
  surveyType?: string;
  surveyTotal: number;
  numberOfRespondents: number | undefined;
  estimatedIncidence: ReactSelectValue<string> | null;
  numberOfQuestions: ReactSelectValue<string> | null;
  baseSubtotal: number;
  methodologyComplexity: string;
  methodologyComplexityOptions: ReactSelectValue<string>[] | undefined;
  methodologyFieldworkGroups: string;
  metholologyFieldworkGroupsOptions: ReactSelectValue<string>[] | undefined;
  surveyDetailsData: {
    ordering: number;
    label: string;
    value: BillingServiceName;
    tooltip: string;
    subtitle: string;
    isSelected: boolean;
    onToggle: () => void;
  }[];
  numberOfGroups: string;
  reportingGroupsOptions: ReactSelectValue<string>[] | undefined;
  reportComplexity: string;
  reportComplexityOptions: ReactSelectValue<string>[] | undefined;
  reportingOptionsData: {
    ordering: number;
    label: string;
    value: BillingServiceName;
    tooltip: string;
    subtitle: string;
    isSelected: boolean;
    onToggle: () => void;
  }[];
  reportingSubtotal: number;
  servicesSubtotal: number;
  activePrices: BillingPrice[] | undefined;
}

const summaryHeaderStyling = 'font-medium text-[#1A3836] mb-3 mt-4';
const summaryLabelStyling = 'text-[#6A7282]';
const summaryDetailStyling = 'text-[#1E2939]';
const summaryTotalsContainerStyling = 'flex justify-between align-center my-2';

const fullServiceFieldingItem = (
  copy: { label: string; tooltip: string },
  index: number,
) => (
  <span
    key={`full-service-fielding-item-${index}`}
    className="flex items-center gap-1 mt-2"
  >
    <div className="flex w-5 h-5 justify-center items-center">
      <div className="w-3 h-3 text-[#2A4F4B]">
        <Icon id="check" />
      </div>
    </div>
    {copy.label}
  </span>
);

const QuoteSummaryPanel = ({
  surveyType,
  surveyTotal,
  numberOfRespondents,
  estimatedIncidence,
  numberOfQuestions,
  baseSubtotal,
  methodologyComplexity,
  methodologyComplexityOptions,
  methodologyFieldworkGroups,
  metholologyFieldworkGroupsOptions,
  surveyDetailsData,
  numberOfGroups,
  reportingGroupsOptions,
  reportComplexity,
  reportComplexityOptions,
  reportingOptionsData,
  reportingSubtotal,
  servicesSubtotal,
  activePrices,
}: QuoteSummaryPanelProps) => {
  const handlePDFDownload = async () => {
    const fileName = `glass-quote-${formatDate(new Date(), { format: DATE_FORMATS.API_DATE_FORMAT })}.pdf`;
    const blob = await pdf(
      <QuoteSummaryPDF
        activePrices={activePrices}
        baseSubtotal={baseSubtotal}
        estimatedIncidence={estimatedIncidence}
        methodologyComplexity={methodologyComplexity}
        methodologyComplexityOptions={methodologyComplexityOptions}
        methodologyFieldworkGroups={methodologyFieldworkGroups}
        metholologyFieldworkGroupsOptions={metholologyFieldworkGroupsOptions}
        numberOfGroups={numberOfGroups}
        numberOfQuestions={numberOfQuestions}
        numberOfRespondents={numberOfRespondents}
        reportComplexity={reportComplexity}
        reportComplexityOptions={reportComplexityOptions}
        reportingGroupsOptions={reportingGroupsOptions}
        reportingOptionsData={reportingOptionsData}
        reportingSubtotal={reportingSubtotal}
        servicesSubtotal={servicesSubtotal}
        surveyDetailsData={surveyDetailsData}
        surveyTotal={surveyTotal}
        surveyType={surveyType}
      />,
    ).toBlob();
    saveAs(blob, fileName);
  };

  return (
    <div className="border-1 border-gray-d-300 bg-white rounded-2xl w-3/4 shrink-0 h-fit self-start sticky top-0 text-sm">
      <div className="bg-primary-d-700 rounded-t-2xl p-8 text-[#8BB5B1] grid grid-cols-2">
        <LogoWhiteText />
        <div className="text-right">
          <p className="text-xs">SURVEY TYPE</p>
          <p className="text-white font-bold mt-1">{surveyType}</p>
        </div>
        <div className="mt-4">
          <p className="text-2xl text-white font-bold mb-1">
            Survey Quote Estimate
          </p>
          <p className="mb-1">
            {formatDate(new Date(), { format: DATE_FORMATS.READABLE_FULL })}
          </p>
        </div>
        <div className="text-right mt-4">
          <p className="text-xs">ESTIMATED TOTAL</p>
          <p className="text-xl text-white font-bold mt-1">
            {formatDollars(surveyTotal)}
          </p>
        </div>
      </div>
      <div className="p-8">
        <p className={summaryHeaderStyling}>SURVEY CONFIGURATION</p>
        <div>
          <div className={summaryTotalsContainerStyling}>
            <p className={summaryLabelStyling}>Survey Type</p>
            <p className="text-[#1E2939] font-bold">{surveyType}</p>
          </div>
          <div className={summaryTotalsContainerStyling}>
            <p className={summaryLabelStyling}>Number of respondents</p>
            <p className={summaryDetailStyling}>
              {numberOfRespondents} respondents
            </p>
          </div>
          <div className={summaryTotalsContainerStyling}>
            <p className={summaryLabelStyling}>Estimated incidence</p>
            <p className={summaryDetailStyling}>
              {estimatedIncidence?.label} incidence
            </p>
          </div>
          <div className={summaryTotalsContainerStyling}>
            <p className={summaryLabelStyling}>Number of questions</p>
            <p className={summaryDetailStyling}>
              {numberOfQuestions?.label} questions
            </p>
          </div>
        </div>
        {surveyType === FULL_SERVICE && (
          <>
            <hr className="mt-4 border-[#F3F4F6]" />
            <div>
              <p className={summaryHeaderStyling}>DESIGN & FIELDING SERVICES</p>
              <p className={summaryLabelStyling}>
                All design and fielding services are included in the base
                fielding cost.
              </p>
              <span className="flex gap-4 justify-between text-[#2A4F4B]">
                <span>
                  {surveyDetailsData
                    .filter((e, i) => i % 2 === 0)
                    .map((copy, index) => fullServiceFieldingItem(copy, index))}
                </span>
                <span>
                  {surveyDetailsData
                    .filter((e, i) => i % 2 !== 0)
                    .map((copy, index) => fullServiceFieldingItem(copy, index))}
                </span>
              </span>
            </div>
          </>
        )}
        {surveyType === HYBRID &&
          surveyDetailsData.some((detail) => detail.isSelected) && (
            <>
              <hr className="mt-4 border-[#F3F4F6]" />
              <div>
                <p className={summaryHeaderStyling}>
                  DESIGN & FIELDING SERVICES
                </p>
                {surveyDetailsData
                  .filter((detail) => detail.isSelected)
                  .map((detail, i) => (
                    <div
                      key={`survey-details-summary-${i}`}
                      className={summaryTotalsContainerStyling}
                    >
                      <p className={summaryLabelStyling}>{detail.label}</p>
                      <p className={summaryDetailStyling}>
                        {getOptionPrice(detail.value, activePrices)}
                      </p>
                    </div>
                  ))}
              </div>
            </>
          )}
        {surveyType !== DIY &&
          (methodologyComplexity || methodologyFieldworkGroups) && (
            <>
              <hr className="mt-4 border-[#F3F4F6]" />
              <div>
                <p className={summaryHeaderStyling}>METHODOLOGY & FIELDWORK</p>
                {methodologyComplexity && (
                  <div className={summaryTotalsContainerStyling}>
                    <p className={summaryLabelStyling}>
                      Methodology Complexity
                    </p>
                    <p className={summaryDetailStyling}>
                      {getOptionLabel(
                        methodologyComplexity,
                        methodologyComplexityOptions,
                      )}
                    </p>
                  </div>
                )}
                {methodologyFieldworkGroups && (
                  <div className={summaryTotalsContainerStyling}>
                    <p className={summaryLabelStyling}>
                      Fieldwork Sample Groups
                    </p>
                    <p className={summaryDetailStyling}>
                      {getOptionLabel(
                        methodologyFieldworkGroups,
                        metholologyFieldworkGroupsOptions,
                      )}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        {reportingOptionsData.some((option) => option.isSelected) && (
          <>
            <hr className="mt-4 border-[#F3F4F6]" />
            <div>
              <p className={summaryHeaderStyling}>REPORTING & ANALYSIS</p>
              {reportingOptionsData
                .filter((option) => option.isSelected)
                .map((option, i) => (
                  <div
                    key={`reporting-options-summary-${i}`}
                    className={summaryTotalsContainerStyling}
                  >
                    <p className={summaryLabelStyling}>{option.label}</p>
                    <p className={summaryDetailStyling}>
                      {getOptionPrice(option.value, activePrices)}
                    </p>
                  </div>
                ))}
              {surveyType !== DIY && reportComplexity && (
                <div className={summaryTotalsContainerStyling}>
                  <p className={summaryLabelStyling}>Reporting Complexity</p>
                  <p className={summaryDetailStyling}>
                    {getOptionLabel(reportComplexity, reportComplexityOptions)}
                  </p>
                </div>
              )}
              {surveyType !== DIY && numberOfGroups && (
                <div className={summaryTotalsContainerStyling}>
                  <p className={summaryLabelStyling}>Groups/Subgroups</p>
                  <p className={summaryDetailStyling}>
                    {getOptionLabel(numberOfGroups, reportingGroupsOptions)}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
        <hr className="mt-4 border-[#F3F4F6]" />
        <p className={summaryHeaderStyling}>COST BREAKDOWN</p>
        <div>
          <div className={summaryTotalsContainerStyling}>
            <p className={summaryLabelStyling}>Base Configuration Cost</p>
            <p className={summaryDetailStyling}>
              {formatDollars(baseSubtotal)}
            </p>
          </div>
          {surveyType !== DIY && (
            <>
              <div className={summaryTotalsContainerStyling}>
                <p className={summaryLabelStyling}>
                  Design & Fielding Services
                </p>
                <p className={summaryDetailStyling}>
                  {surveyType === FULL_SERVICE
                    ? 'Included'
                    : formatDollars(servicesSubtotal)}
                </p>
              </div>
              <div className={summaryTotalsContainerStyling}>
                <p className={summaryLabelStyling}>Reporting & Analysis</p>
                <p className={summaryDetailStyling}>
                  {formatDollars(reportingSubtotal)}
                </p>
              </div>
            </>
          )}
        </div>
        <hr className="mt-4 border-[#F3F4F6]" />
        <div className="flex justify-between align-center rounded-xl bg-[#F0F7F6] border-2 border-[#C5DEDD] p-4 mt-6">
          <div>
            <p className="font-semibold text-[#4A7A76] my-2">ESTIMATED TOTAL</p>
            <p className="text-[#99A1AF] my-2">
              All figures are estimates and subject to final review
            </p>
          </div>
          <p className="font-semibold text-[#1A3836] my-4 text-3xl">
            {formatDollars(surveyTotal)}
          </p>
        </div>
        <p className="text-[#6A7282] text-xs text-center my-4">
          This estimate is generated based on your inputs and is subject to
          change. A Glass Research Director will confirm the final scope and
          pricing before project kickoff.
        </p>
        <div className="flex justify-center">
          <Button
            grow
            hierarchy={'primary'}
            onClick={handlePDFDownload}
            size="lg"
          >
            Download Quote PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuoteSummaryPanel;
